import React, { Fragment, ReactElement, ReactNode } from 'react';
import { LinkComponent } from '../../';
import {
    IMessageOptions,
    IServerNotificationAPIGETPayload,
    NotificationType,
    ServerNotificationEventDataImportTypeEnum,
    ServerNotificationEventTypeEnum,
} from '../../../interfaces';
import { useProfile } from '../../../hooks';
import { appSignalSendMessage, mapApi2UiField } from '../../../instruments';
import { ITrServerNotification, tCustom, tServerNotification } from '../../../translate';

type INotificationMessage = {
    id: string;
    event: ITrServerNotification;
    payload: IServerNotificationAPIGETPayload;
};

type IAddLinks = {
    message: string;
    options: IMessageOptions;
};

const addLink = (arrMessage: ReactNode[], text: string, link: ReactNode): ReactNode[] => {
    const newMessage: ReactNode[] = [];
    arrMessage.forEach((subMess) => {
        if (typeof subMess === 'string') {
            if (subMess.includes(text)) {
                const newArr = subMess.split(text);
                newArr.forEach((sm: string, i: number) => {
                    newMessage.push(sm);
                    if (i < newArr.length - 1) {
                        newMessage.push(link);
                    }
                });
            } else {
                newMessage.push(subMess);
            }
        } else {
            newMessage.push(subMess);
        }
    });

    return newMessage;
};

const wrapId = (id: string | number): string => `#${id}#`;

const AddLinks: React.FC<IAddLinks> = ({ message, options }: IAddLinks): ReactElement => {
    let newMessage: ReactNode[] = [message];

    if (options.customLinkId && options.customLinkListComponent) {
        const customLinkId = options.customLinkId.toString();
        if (options.customLinkName) {
            newMessage = addLink(
                newMessage,
                wrapId(options.customLinkName),
                <LinkComponent
                    currentListComponent={options.customLinkListComponent}
                    id={customLinkId}
                    name={options.customLinkName}
                />,
            );
        }
        newMessage = addLink(
            newMessage,
            wrapId(options.customLinkId),
            <LinkComponent
                currentListComponent={options.customLinkListComponent}
                id={customLinkId}
                name={options.customLinkName}
            />,
        );
    }

    if (options.cardId) {
        const cardId = options.cardId.toString();
        if (options.eid) {
            newMessage = addLink(
                newMessage,
                wrapId(options.eid),
                <LinkComponent currentListComponent={'CardList'} id={cardId} name={options.eid} />,
            );
        }
        if (options.lockImei) {
            newMessage = addLink(
                newMessage,
                wrapId(options.lockImei),
                <LinkComponent currentListComponent={'CardList'} id={cardId} name={options.lockImei} />,
            );
        }
        if (options.lastImei) {
            newMessage = addLink(
                newMessage,
                wrapId(options.lastImei),
                <LinkComponent currentListComponent={'CardList'} id={cardId} name={options.lastImei} />,
            );
        }
        newMessage = addLink(
            newMessage,
            wrapId(options.cardId),
            <LinkComponent
                currentListComponent={'CardList'}
                id={cardId}
                name={options.lockImei || options.lastImei || options.eid}
            />,
        );
    }

    if (options.subscriberId) {
        const subscriberId = options.subscriberId.toString();
        if (options.msisdn) {
            newMessage = addLink(
                newMessage,
                wrapId(options.msisdn),
                <LinkComponent currentListComponent={'SubscribersList'} id={subscriberId} name={options.msisdn} />,
            );
        }
        newMessage = addLink(
            newMessage,
            wrapId(subscriberId),
            <LinkComponent currentListComponent={'SubscribersList'} id={subscriberId} name={options.msisdn} />,
        );
    }

    if (options.operatorId) {
        const operatorId = options.operatorId.toString();
        if (options.tadig) {
            const operatorId = options.operatorId.toString();
            newMessage = addLink(
                newMessage,
                wrapId(options.tadig),
                <LinkComponent currentListComponent={'OperatorsList'} id={operatorId} name={options.tadig} />,
            );
        }
        newMessage = addLink(
            newMessage,
            wrapId(operatorId),
            <LinkComponent currentListComponent={'OperatorsList'} id={operatorId} name={options.tadig} />,
        );
    }

    if (options.cardSetId) {
        const cardSetId = options.cardSetId.toString();
        newMessage = addLink(
            newMessage,
            wrapId(cardSetId),
            <LinkComponent currentListComponent={'CardSetsList'} id={cardSetId} />,
        );
    }

    if (options.salesRateListId) {
        const salesRateListId = options.salesRateListId.toString();
        newMessage = addLink(
            newMessage,
            wrapId(salesRateListId),
            <LinkComponent
                currentListComponent={'SalesRateLists'}
                id={salesRateListId}
                name={options.salesRateListName}
            />,
        );
    }

    return (
        <Fragment>
            {newMessage.map((sm: ReactNode, i: number) => (
                <Fragment key={i}>{sm}</Fragment>
            ))}
        </Fragment>
    );
};

const makeCustomLinkAndTextType = ({ id, event, payload }: INotificationMessage): IMessageOptions => {
    const { type } = payload;
    const options: IMessageOptions = { id, type };
    switch (event) {
        case ServerNotificationEventTypeEnum.EIM_PROFILE_CREATED:
        case ServerNotificationEventTypeEnum.EIM_PROFILE_UPDATED:
        case ServerNotificationEventTypeEnum.EIM_PROFILE_DESTROYED:
            options.type = event;
            options.cardId = payload.card?.id;
            options.eid = payload.card?.eid;
            options.operatorId = payload.operator?.id;
            options.tadig = payload.operator?.tadig;
            options.subscriberId = payload.subscriber?.id;
            options.msisdn = payload.subscriber?.msisdn;
            break;
        case ServerNotificationEventTypeEnum.DATA_IMPORT_FAILED:
        case ServerNotificationEventTypeEnum.DATA_IMPORT_PERFORMED:
            switch (type) {
                case ServerNotificationEventDataImportTypeEnum.SALES_RATE_LIST_ENTRIES:
                    options.type = 'Sales Rate List';
                    if (payload.sales_rate_list_id) {
                        options.customLinkId = payload.sales_rate_list_id.toString();
                        options.customLinkListComponent = 'SalesRateLists';
                        options.customLinkName = payload.sales_rate_list_name;
                    }
                    return options;
                case ServerNotificationEventDataImportTypeEnum.SUBSCRIBER_IDENTITIES:
                    options.type = 'Subscriber Identities';
                    if (payload.subscriber_id) {
                        options.customLinkId = payload.subscriber_id.toString();
                        options.customLinkListComponent = 'SubscribersList';
                        options.customLinkName = payload.subscriber_name;
                    }
                    return options;
                default:
                    const description = `Unknown type of Data import message id: "${id}" type: "${type}" event: "${event}"`;
                    if (process.env.NODE_ENV !== 'development') {
                        appSignalSendMessage({
                            action: 'NotificationMessage',
                            message: 'Unknown type of message',
                            description,
                            params: { id, event, payload: JSON.stringify(payload) },
                            appSignalType: NotificationType.WARNING,
                            error: new Error(`Unknown type of Data import message: ${event}`),
                        });
                    } else {
                        console.warn(description, 'payload:', { ...payload });
                    }
            }
            break;
        default:
            if (type) {
                const description = `Unknown type of message id: "${id}" type: "${type}" event: "${event}"`;
                if (process.env.NODE_ENV !== 'development') {
                    appSignalSendMessage({
                        action: 'NotificationMessage',
                        message: 'Unknown type of message',
                        description,
                        params: { id, event, payload: JSON.stringify(payload) },
                        appSignalType: NotificationType.WARNING,
                        error: new Error(`Unknown type of message: ${event}`),
                    });
                } else {
                    console.warn(description, 'payload:', { ...payload });
                }
            }
    }

    return options;
};

export const NotificationMessage: React.FC<INotificationMessage> = ({ id, event, payload }: INotificationMessage) => {
    const { currency = '' } = useProfile();
    const options: IMessageOptions = {
        ...mapApi2UiField(payload),
        ...makeCustomLinkAndTextType({ id, event, payload }),
        currency,
    };

    const message = tServerNotification(event, options);
    if (message === event) {
        const description = `Unidentified message from server id: ${id} event: ${event}`;
        console.warn(description);
        appSignalSendMessage({
            action: 'NotificationMessage',
            message: `Unidentified message from server id: ${id}`,
            description,
            params: { id, event, payload: JSON.stringify(payload) },
            appSignalType: NotificationType.WARNING,
            error: new Error(`Unidentified message from server: ${event}`),
        });
    }
    const transactionMessage = message === event ? tCustom('Unidentified message from server', { id: id }) : message;

    return <AddLinks message={transactionMessage} options={options} />;
};
